import {memo, useCallback} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {
  TextBodyMedium,
  TextBodySmall,
  TextHeading6,
  TextHeading2,
} from '../index';
import {useI18n} from '../../hooks';

interface ParticipantSliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SliderContainer = styled.div`
  position: relative;
  margin: 0px 0;
`;

const Slider = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e0e0e0;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${Palette.primaryTerraCotta};
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${Palette.primaryTerraCotta};
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }

  &::-webkit-slider-track {
    height: 8px;
    border-radius: 4px;
    background: #e0e0e0;
  }

  &::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: #e0e0e0;
  }

  &::-moz-range-progress {
    height: 8px;
    border-radius: 4px;
    background: ${Palette.primaryTerraCotta};
  }
`;

const ValueDisplay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const ValueNumber = styled.div`
  min-width: 30px;
  text-align: center;
`;

const LabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  height: 100%;
  align-items: center;
`;

const Label = styled.div`
  display: flex;
  text-align: center;
  padding: 4px 8px;
  border-radius: 4px;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
`;

export const ParticipantSlider = memo(
  ({value, onChange, min = 0, max = 100}: ParticipantSliderProps) => {
    const t = useI18n();
    // Convert slider position to actual value
    // Slider: 0 -> Value: 0 (unlimited)
    // Slider: 1 -> Value: 3
    // Slider: 2 -> Value: 4
    // etc.
    const sliderToValue = useCallback((sliderValue: number) => {
      if (sliderValue === 0) return 0; // unlimited
      return sliderValue + 2; // skip 1 and 2, start from 3
    }, []);

    // Convert actual value to slider position
    const valueToSlider = useCallback((actualValue: number) => {
      if (actualValue === 0) return 0; // unlimited
      if (actualValue < 3) return 1; // treat 1,2 as 3
      return actualValue - 2; // adjust for skipped values
    }, []);

    const sliderValue = valueToSlider(value);
    const maxSliderValue = max - 2; // adjust max for skipped values

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const sliderVal = parseInt(e.target.value, 10);
        const actualValue = sliderToValue(sliderVal);
        onChange(actualValue);
      },
      [onChange, sliderToValue],
    );

    const getValueLabel = useCallback(() => {
      if (value === 0) return t.common.unlimitedColleagues;
      if (value === 1) return t.common.colleague;
      return t.common.colleagues;
    }, [value, t]);

    return (
      <Container>
        <TextHeading6 text={t.events.maxParticipants} />
        <TextBodyMedium
          text={t.events.maxParticipantsDescription}
          color={Palette.almostBlack500}
        />

        <SliderContainer>
          <Slider
            type="range"
            min={0}
            max={maxSliderValue}
            value={sliderValue}
            onChange={handleChange}
          />
        </SliderContainer>

        <LabelsContainer>
          <Label>
            <TextBodySmall text={t.events.unlimited} />
          </Label>

          <ValueDisplay>
            <ValueNumber>
              <TextHeading2
                text={value === 0 ? '∞' : value.toString()}
                color={Palette.primaryTerraCotta}
              />
            </ValueNumber>
            <TextBodyMedium
              text={getValueLabel()}
              color={Palette.almostBlack750}
            />
          </ValueDisplay>
          <Label>
            <TextBodySmall text={`3-${max} ${t.common.peopleRange}`} />
          </Label>
        </LabelsContainer>
      </Container>
    );
  },
);
